/**
 * Editor di coppie e gironi (fase di setup).
 *
 * Due modi di spostare, entrambi funzionano col dito:
 * - trascina dalla maniglia ⋮⋮ (giocatore → altro posto, coppia → altro girone);
 * - tocca un giocatore, poi tocca il posto dove metterlo (scambio).
 * Ogni modifica viene salvata subito sul server.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Ellipsis, GripVertical, Plus, UserCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import type { GaraState, LineupTeam, Player } from "@/lib/gare";
import { lvl } from "./ui";

type Item = { kind: "player"; pid: number } | { kind: "team"; t: number };
type Target =
  | { kind: "slot"; t: number; s: 1 | 2; g: string }
  | { kind: "pool" }
  | { kind: "pp"; pid: number } // un giocatore senza coppia
  | { kind: "group"; g: string };

function toLocal(state: GaraState): LineupTeam[] {
  return [...state.teams]
    .sort((a, b) => (a.group_label || "").localeCompare(b.group_label || "") || (a.seed || 0) - (b.seed || 0))
    .map((t) => ({ p1: t.p1, p2: t.p2, group_label: t.group_label || "A", origin: t.origin }));
}

export function Lineup({ state, onSave, onPlayerMenu, onRestore, single }: {
  state: GaraState;
  single?: boolean; // coppie fisse: un solo elenco di coppie, niente gironi
  onSave: (teams: LineupTeam[]) => void;
  onPlayerMenu: (p: Player) => void;
  onRestore: (p: Player) => void;
}) {
  const [teams, setTeams] = useState<LineupTeam[]>(() => toLocal(state));
  useEffect(() => setTeams(toLocal(state)), [state]);
  const [sel, setSel] = useState<Item | null>(null);
  const [ghost, setGhost] = useState<{ x: number; y: number; label: string } | null>(null);
  const dragItem = useRef<Item | null>(null);

  const players = useMemo(() => Object.fromEntries(state.players.map((p) => [p.id, p])), [state.players]);
  const placed = new Set(teams.flatMap((t) => [t.p1, t.p2]).filter((x): x is number => !!x));
  const pool = state.players.filter((p) => p.role === "player" && !placed.has(p.id));
  const fakes = state.players.filter((p) => p.role === "fake");
  const nGroups = Math.max(1, state.gara.config.groups || 1);
  const labels = Array.from(new Set(["ABCDEFGH".slice(0, nGroups).split(""), teams.map((t) => t.group_label || "A")].flat())).sort();

  const apply = (next: LineupTeam[]) => {
    setTeams(next);
    if (next.some((t) => t.p1 || t.p2)) onSave(next.filter((t) => t.p1 || t.p2));
  };

  function drop(item: Item, target: Target) {
    const next = teams.map((t) => ({ ...t }));
    if (item.kind === "team") {
      const g = target.kind === "slot" || target.kind === "group" ? target.g : null;
      if (g && next[item.t].group_label !== g) {
        next[item.t].group_label = g;
        apply(next);
      }
      return;
    }
    const pid = item.pid;
    let src: { t: number; s: 1 | 2 } | null = null;
    next.forEach((t, i) => {
      if (t.p1 === pid) src = { t: i, s: 1 };
      if (t.p2 === pid) src = { t: i, s: 2 };
    });
    const from = src as { t: number; s: 1 | 2 } | null;
    if (target.kind === "pool") {
      if (from) {
        next[from.t][from.s === 1 ? "p1" : "p2"] = null;
        apply(next);
      }
      return;
    }
    if (target.kind === "pp") { // scambio con un giocatore senza coppia
      if (from && target.pid !== pid) {
        next[from.t][from.s === 1 ? "p1" : "p2"] = target.pid;
        apply(next);
      }
      return;
    }
    if (target.kind !== "slot") return;
    if (from && from.t === target.t && from.s === target.s) return;
    const key = target.s === 1 ? "p1" : "p2";
    const occupant = next[target.t][key];
    next[target.t][key] = pid;
    if (from) next[from.t][from.s === 1 ? "p1" : "p2"] = occupant;
    apply(next);
  }

  // ---- trascinamento con pointer events (mouse e touch)
  useEffect(() => {
    if (!ghost) return;
    const move = (e: PointerEvent) => setGhost((g) => (g ? { ...g, x: e.clientX, y: e.clientY } : g));
    const up = (e: PointerEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY)?.closest("[data-drop]") as HTMLElement | null;
      const item = dragItem.current;
      dragItem.current = null;
      setGhost(null);
      if (el?.dataset.drop && item) drop(item, JSON.parse(el.dataset.drop) as Target);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ghost !== null]);

  const startDrag = (e: React.PointerEvent, item: Item, label: string) => {
    e.preventDefault();
    dragItem.current = item;
    setSel(null);
    setGhost({ x: e.clientX, y: e.clientY, label });
  };

  const tapTarget = (target: Target) => {
    if (sel) {
      drop(sel, target);
      setSel(null);
    }
  };

  const chip = (pid: number, target: Target) => {
    const p = players[pid];
    if (!p) return null;
    const selected = sel?.kind === "player" && sel.pid === pid;
    return (
      <div key={pid} className={cn("flex items-center gap-1 rounded-lg border bg-white px-1 py-1",
        selected ? "border-moving-500 ring-2 ring-moving-500" : "border-slate-200")}>
        <span onPointerDown={(e) => startDrag(e, { kind: "player", pid }, p.name)} aria-label={`Trascina ${p.name}`}
          className="flex h-8 w-6 shrink-0 cursor-grab touch-none items-center justify-center text-slate-400">
          <GripVertical className="h-4 w-4" />
        </span>
        <button type="button" className="min-w-0 flex-1 text-left"
          onClick={() => (sel ? (selected ? setSel(null) : tapTarget(target)) : setSel({ kind: "player", pid }))}>
          <span className="block truncate text-sm font-medium text-slate-900">{p.name}</span>
          <span className="block text-xs text-slate-500">
            {p.level !== null ? `liv. ${lvl(p.level)}` : "livello ?"}{p.source === "manual" ? " · aggiunto" : ""}
          </span>
        </button>
        <button type="button" aria-label={`Opzioni ${p.name}`} onClick={() => onPlayerMenu(p)}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100">
          <Ellipsis className="h-4 w-4" />
        </button>
      </div>
    );
  };

  const slot = (ti: number, s: 1 | 2, g: string) => {
    const pid = s === 1 ? teams[ti].p1 : teams[ti].p2;
    const target: Target = { kind: "slot", t: ti, s, g };
    return (
      <div key={s} data-drop={JSON.stringify(target)} className="min-h-[3rem]">
        {pid ? chip(pid, target) : (
          <button type="button" onClick={() => tapTarget(target)}
            className={cn("flex h-12 w-full items-center justify-center rounded-lg border-2 border-dashed text-xs",
              sel?.kind === "player" ? "border-moving-500 bg-moving-50 text-moving-700" : "border-slate-200 text-slate-400")}>
            {sel?.kind === "player" ? "Metti qui" : "Posto libero"}
          </button>
        )}
      </div>
    );
  };

  const avg = (t: LineupTeam) => {
    const v = [t.p1, t.p2].map((x) => (x ? players[x]?.level : null)).filter((x): x is number => x !== null && x !== undefined);
    return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        Trascina dalla maniglia <GripVertical className="inline h-3.5 w-3.5" /> oppure tocca un giocatore e poi il posto dove spostarlo.
        Le modifiche si salvano da sole.
      </p>

      <div className={cn("grid gap-4", labels.length > 1 && "lg:grid-cols-2")}>
        {labels.map((g) => {
          const idx = teams.map((t, i) => ({ t, i })).filter((x) => (x.t.group_label || "A") === g);
          return (
            <div key={g} data-drop={JSON.stringify({ kind: "group", g })}
              className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-900">{single ? "Coppie" : `Girone ${g}`}</h3>
                <span className="text-xs text-slate-500">{idx.filter((x) => x.t.p1 && x.t.p2).length} coppie</span>
              </div>
              <div className="space-y-2">
                {idx.map(({ t, i }, n) => {
                  const selTeam = sel?.kind === "team" && sel.t === i;
                  return (
                    <div key={i} className={cn("rounded-lg border bg-white p-2",
                      selTeam ? "border-moving-500 ring-2 ring-moving-500" : t.p1 && t.p2 ? "border-slate-200" : "border-amber-300")}>
                      <div className="mb-1.5 flex items-center gap-1 text-xs text-slate-500">
                        <span onPointerDown={(e) => startDrag(e, { kind: "team", t: i }, `Coppia ${n + 1}`)}
                          aria-label="Sposta la coppia in un altro girone"
                          className="flex h-7 w-6 cursor-grab touch-none items-center justify-center text-slate-400">
                          <GripVertical className="h-4 w-4" />
                        </span>
                        <span className="font-medium text-slate-700">Coppia {n + 1}</span>
                        {avg(t) !== null ? <span>· media {lvl(avg(t))}</span> : null}
                        {t.origin === "playtomic" ? <span className="rounded bg-sky-50 px-1.5 text-sky-700">iscritti insieme</span> : null}
                        {!(t.p1 && t.p2) ? <span className="rounded bg-amber-50 px-1.5 text-amber-700">incompleta</span> : null}
                        {labels.length > 1 ? (
                          <button type="button" className="ml-auto rounded px-1.5 py-0.5 text-slate-600 hover:bg-slate-100"
                            onClick={() => drop({ kind: "team", t: i }, { kind: "group", g: labels.find((x) => x !== g) || g })}>
                            → girone {labels.find((x) => x !== g)}
                          </button>
                        ) : null}
                      </div>
                      <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                        {slot(i, 1, g)}
                        {slot(i, 2, g)}
                      </div>
                    </div>
                  );
                })}
                <button type="button"
                  onClick={() => setTeams((ts) => [...ts, { p1: null, p2: null, group_label: g, origin: "manual" }])}
                  className="flex w-full items-center justify-center gap-1 rounded-lg border-2 border-dashed border-slate-200 py-2 text-xs text-slate-500 hover:border-slate-300">
                  <Plus className="h-3.5 w-3.5" /> Nuova coppia vuota
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div data-drop={JSON.stringify({ kind: "pool" })} onClick={() => sel?.kind === "player" && tapTarget({ kind: "pool" })}
        className={cn("rounded-xl border-2 border-dashed p-3", sel?.kind === "player" ? "border-moving-500 bg-moving-50" : "border-slate-200")}>
        <div className="mb-2 text-sm font-semibold text-slate-900">Senza coppia ({pool.length})</div>
        {pool.length ? (
          <div className="grid gap-1.5 sm:grid-cols-2" onClick={(e) => e.stopPropagation()}>
            {pool.map((p) => (
              <div key={p.id} data-drop={JSON.stringify({ kind: "pp", pid: p.id })}>{chip(p.id, { kind: "pp", pid: p.id })}</div>
            ))}
          </div>
        ) : <p className="text-xs text-slate-500">Tutti i giocatori hanno un compagno. Trascina qui chi vuoi togliere da una coppia.</p>}
      </div>

      {fakes.length ? (
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="mb-2 text-sm font-semibold text-slate-900">Finti / non giocano ({fakes.length})</div>
          <div className="flex flex-wrap gap-2">
            {fakes.map((p) => (
              <button key={p.id} type="button" onClick={() => onRestore(p)}
                className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm text-slate-500 line-through hover:bg-slate-50">
                <UserCheck className="h-4 w-4" /> {p.name}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-slate-500">Tocca un nome per rimetterlo in gioco.</p>
        </div>
      ) : null}

      {ghost ? (
        <div className="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-1/2 rounded-lg bg-moving-500 px-3 py-2 text-sm font-semibold text-white shadow-lg"
          style={{ left: ghost.x, top: ghost.y }}>
          {ghost.label}
        </div>
      ) : null}
    </div>
  );
}
